import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useBabyProfiles } from '../../contexts/BabyProfileContext';
// Remove unused import
// import PlanBadge from '../../components/common/PlanBadge';
import AccountInfoCard from '../../components/AccountInfoCard';
import RecommendedProducts from '../../components/RecommendedProducts';
import TrustIndicator from '../../components/TrustIndicator';
import { useLogger } from '../../utils/logger';
import { babyProfileService } from '../../services/babyProfileService';
import CollapsibleSafetyGuidelines from '../../components/disclaimers/CollapsibleSafetyGuidelines';

// Styled components from original file...
const DashboardContainer = styled.div`
  padding: 2rem 0;
`;

const WelcomeSection = styled.div`
  background-color: var(--primary);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 150px;
    height: 150px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    transform: translate(50%, -50%);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const WelcomeContent = styled.div`
  flex: 1;
  z-index: 1;
`;

const WelcomeImageContainer = styled.div`
  flex: 0 0 200px;
  margin-left: 2rem;
  z-index: 1;
  
  @media (max-width: 991px) {
    display: none; /* Hide on mobile */
  }
`;

const WelcomeImage = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }
`;

const WelcomeTitle = styled.h1`
  margin-bottom: 1rem;
  font-size: 2rem;
`;

const WelcomeText = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  max-width: 600px;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 3fr 1fr;
  }
`;

const MainContent = styled.div`
  /* No special styling for family plan with two children */
`;
const Sidebar = styled.div`
  @media (max-width: 991px) {
    display: none; /* Hide sidebar completely on mobile */
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: var(--dark);
  font-size: 1.25rem;
`;

const ChildrenList = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 992px) {
    /* Use conditional grid layout based on number of children and plan type */
    grid-template-columns: ${props => 
      // If it's a family plan with 2 children, stack them
      (props.isFamilyPlan && props.childCount === 2) 
        ? '1fr' 
        : '1fr 1fr'
    };
    
    /* Make the cards narrower when stacking cards for family plan, but align left */
    ${props => (props.isFamilyPlan && props.childCount === 2) && `
      & > * {
        max-width: 85%;
        margin-right: auto;
        margin-left: 0;
      }
    `}
  }
  
  /* Ensure each child card has enough space for buttons */
  & > * {
    min-width: 0; /* Allow children to shrink if needed */
    width: 100%; /* Ensure cards take full width of their grid cell */
  }
`;

const ChildCard = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 1.25rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%; /* Make all cards the same height */
  width: 100%; /* Ensure card takes full width */
  box-sizing: border-box; /* Include padding in width calculation */
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
  
  /* Ensure the card has enough width for buttons */
  @media (min-width: 992px) {
    min-width: auto; /* Let it be responsive based on container */
    max-width: 100%; /* Ensure it doesn't overflow */
  }
  
  @media (max-width: 768px) {
    min-width: auto; /* Let it be responsive on mobile */
  }
`;

const ChildHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const GenderIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-left: 8px;
`;

const ChildName = styled.h4`
  margin: 0 0 0.5rem;
  color: var(--dark);
`;

const ChildAge = styled.p`
  margin: 0 0 1rem;
  color: var(--gray);
  font-size: 0.9rem;
  flex-grow: 1; /* Allow this element to grow and push buttons to bottom */
`;

// Remove unused StatusBadge component
/* const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1rem;
  
  ${props => props.status === 'completed' && `
    background-color: #d1e7dd;
    color: #0f5132;
  `}
  
  ${props => props.status === 'in-progress' && `
    background-color: #fff3cd;
    color: #664d03;
  `}
  
  ${props => props.status === 'not-started' && `
    background-color: #f8d7da;
    color: #842029;
  `}
`; */

const ChildActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap; /* Allow buttons to wrap if needed */
  margin-top: auto; /* Push the buttons to the bottom of the card */
  width: 100%; /* Make sure it takes full width of the card */
  
  @media (max-width: 768px) {
    flex-direction: column; /* Stack buttons on mobile instead of wrapping */
  }
`;

const ActionButton = styled(Link)`
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  text-align: center;
  transition: background-color 0.3s ease;
  white-space: nowrap; /* Prevent text from wrapping */
  flex: 1; /* Make buttons take equal width */
  
  ${props => props.primary && `
    background-color: var(--primary);
    color: white;
    
    &:hover {
      background-color: var(--primary-dark);
    }
  `}
  
  ${props => props.secondary && `
    background-color: white;
    color: var(--primary);
    border: 1px solid var(--primary);
    
    &:hover {
      background-color: #f8f9fa;
    }
  `}
`;

const QuickActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const QuickActionButton = styled(Link)`
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  text-decoration: none;
  color: var(--dark);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }
  
  svg {
    margin-right: 1rem;
    color: var(--primary);
  }
`;

const ActionText = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
  }
  
  p {
    margin: 0;
    font-size: 0.85rem;
    color: var(--gray);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 12px;
  
  h4 {
    margin: 1rem 0;
    color: var(--dark);
  }
  
  p {
    margin-bottom: 1.5rem;
    color: var(--gray);
  }
`;

const AddChildButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: var(--primary);
  color: white;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.2s ease;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  
  p {
    color: var(--gray);
  }
`;

const StartNowButton = styled(Link)`
  display: inline-block;
  padding: 0.75rem 2rem;
  background-color: white;
  color: var(--primary);
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.2s ease;
  margin-top: 1rem;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
    transform: translateY(-2px);
  }
`;

// Remove unused PlanLimitMessage component
// const PlanLimitMessage = styled.div`
//   background-color: #f8f9fa;
//   color: #664d03;
//   padding: 0.75rem;
//   border-radius: 8px;
//   margin-top: 1rem;
//   font-size: 0.85rem;
//   text-align: center;
//   border: 1px dashed #ffc107;
// `;

const EditLimitMessage = styled.span`
  display: block;
  text-align: center;
  color: #6c757d;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  font-style: italic;
`;

const Dashboard = () => {
  const { currentUser, signOut } = useAuth();
  // Remove unused variables from destructuring
  const { firstName, plan, loading: userLoading, userData, error: userError } = useUser();
  const { profiles, loading: profilesLoading, error: profilesError } = useBabyProfiles();
  const navigate = useNavigate();
  const [retryCount, setRetryCount] = useState(0);
  const logger = useLogger('Dashboard');
  
  // State for random baby image
  const [babyImage, setBabyImage] = useState('');
  
  // Set random baby image on component mount
  useEffect(() => {
    // Array of baby images to rotate
    const babyImages = [
      '/images/michael-mcauliffe-woePzpqbieM-unsplash.jpg',
      '/images/minnie-zhou-0hiUWSi7jvs-unsplash.jpg',
      '/images/newborn-5036843_1280.jpg',
      '/images/kelly-sikkema-t53nC7J6Y0E-unsplash.jpg'
    ];
    
    // Select a random image
    const randomIndex = Math.floor(Math.random() * babyImages.length);
    logger.debug("Selected baby image:", babyImages[randomIndex]); // Debug log
    setBabyImage(babyImages[randomIndex]);
  }, [logger]); // Add logger to dependencies
  
  // We're no longer using localStorage for child profiles
  
  // Debug logging
  useEffect(() => {
    logger.debug('Dashboard state', { 
      hasUserData: !!userData, 
      userLoading, 
      hasUserError: !!userError, 
      retryCount,
      profilesCount: profiles?.length,
      profilesLoading,
      hasProfilesError: !!profilesError
    });
  }, [userData, userLoading, userError, retryCount, profiles, profilesLoading, profilesError, logger]);
  
  // Check if user data is missing and handle accordingly
  useEffect(() => {
    // Only check after loading is complete and we've tried a few times
    if (!userLoading && retryCount >= 5) {
      // If there's no user data but we have a current user, there's a problem
      if (!userData && currentUser) {
        logger.warn('User data is missing after retries, redirecting to login');
        // Sign out the user
        signOut();
        // Redirect to login with error message
        navigate('/login', { 
          state: { 
            error: "We couldn't find your account data. Please sign in again to resolve this issue. If the problem persists, please contact support@dozzy.au for assistance.",
            errorTitle: "Account Data Not Found"
          },
          replace: true 
        });
      }
    } else if (!userLoading && !userData && currentUser) {
      // If we're still within retry attempts, try again
      logger.debug(`Retry attempt ${retryCount + 1} to get user data`);
      setRetryCount(prev => prev + 1);
      
      // Force a refresh of the UserContext
      const refreshTimeout = setTimeout(() => {
        // This will trigger the useEffect in UserContext
        const timestamp = Date.now().toString();
        sessionStorage.setItem('forceRefresh', timestamp);
        // Also dispatch a storage event for the current window
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'forceRefresh',
          newValue: timestamp
        }));
      }, 1000); // 1 second delay
      
      return () => clearTimeout(refreshTimeout);
    }
  }, [userLoading, userData, currentUser, navigate, signOut, retryCount, logger]);
  
  // Show loading state during retries
  if (!userLoading && !userData && currentUser && retryCount < 5) {
    return (
      <div className="container">
        <div className="text-center py-5">
          <h3>Loading your dashboard...</h3>
          <p>Please wait while we retrieve your information.</p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3">
            {retryCount > 2 ? "This is taking longer than expected. Please be patient..." : ""}
          </p>
        </div>
      </div>
    );
  }
  
  // Check if user can add more children based on their plan
  const canAddMoreChildren = () => {
    if (plan === 'family') {
      return profiles.length < 2;
    } else {
      // Basic plan or any other plan
      return profiles.length < 1;
    }
  };
  
  // Remove unused function
  // const shouldShowUpgradeMessage = () => {
  //   return plan !== 'family' && profiles.length >= 1;
  // };
  
  if (userLoading || profilesLoading) {
    return (
      <div className="container">
        <DashboardContainer>
          <LoadingState>
            <p>Loading your dashboard...</p>
          </LoadingState>
        </DashboardContainer>
      </div>
    );
  }
  
  return (
    <div className="container">
      <DashboardContainer>
        <WelcomeSection>
          <WelcomeContent>
            <WelcomeTitle>Welcome, {firstName || 'there'}!</WelcomeTitle>
            <WelcomeText>
              Let's create a sleep plan tailored for your little dreamer. It only takes a few minutes.
            </WelcomeText>
            {profiles.length === 0 && (
              <StartNowButton to="/add-child">Start Now</StartNowButton>
            )}
          </WelcomeContent>
          <WelcomeImageContainer>
            <WelcomeImage 
              src={babyImage || "/images/kelly-sikkema-t53nC7J6Y0E-unsplash.jpg"} 
              alt="Sleeping baby" 
            />
          </WelcomeImageContainer>
        </WelcomeSection>
        
        <DashboardGrid>
          <MainContent>
            <Card>
              <CardHeader>
                <CardTitle>Your Children</CardTitle>
                {canAddMoreChildren() && (
                  <AddChildButton to="/add-child">Add Child</AddChildButton>
                )}
              </CardHeader>
              
              {profiles.length > 0 ? (
                <ChildrenList isFamilyPlan={plan === 'family'} childCount={profiles.length}>
                  {profiles.map(profile => (
                    <ChildCard key={profile.profileId}>
                      <ChildHeader>
                        <ChildName>{profile.babyName}</ChildName>
                        <GenderIcon 
                          src={profile.gender === 'Male' ? '/images/baby-boy.png' : '/images/baby-girl.png'} 
                          alt={profile.gender === 'Male' ? 'Boy' : 'Girl'} 
                        />
                      </ChildHeader>
                      <ChildAge>{babyProfileService.calculateAge(profile.dateOfBirth)}</ChildAge>
                      <ChildActions>
                        <ActionButton 
                          to={`/view-sleep-plan/${profile.profileId}`} 
                          primary
                        >
                          View Sleep Plan
                        </ActionButton>
                        
                        <ActionButton 
                          to={`/child/${profile.profileId}`} 
                          state={{ profile: profile }}
                          secondary
                        >
                          View Profile
                        </ActionButton>
                        
                        {(!profile.editCount || profile.editCount < 3) ? (
                          <ActionButton 
                            to={`/edit-child/${profile.profileId}`} 
                            secondary
                          >
                            Edit Profile
                          </ActionButton>
                        ) : (
                          <EditLimitMessage>
                            Edit limit reached (3/3)
                          </EditLimitMessage>
                        )}
                      </ChildActions>
                    </ChildCard>
                  ))}
                </ChildrenList>
              ) : (
                <EmptyState>
                  <h4>No children added yet</h4>
                  <p>Add your first child to get started with a personalized sleep plan</p>
                  <AddChildButton to="/add-child">Add Your First Child</AddChildButton>
                </EmptyState>
              )}
              
              {/* Add Collapsible Safety Guidelines right after the children card */}
              <CollapsibleSafetyGuidelines />
            </Card>
            
            
            
            {/* Add TrustIndicator component after the Safety Guidelines */}
            <TrustIndicator />
            
          </MainContent>
          
          <Sidebar>
            <AccountInfoCard />
            
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              
              <QuickActions>
                {canAddMoreChildren() && (
                  <QuickActionButton to="/add-child">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <line x1="19" y1="8" x2="19" y2="14"></line>
                      <line x1="16" y1="11" x2="22" y2="11"></line>
                    </svg>
                    <ActionText>
                      <h4>Add Child</h4>
                      <p>Create a profile for another child</p>
                    </ActionText>
                  </QuickActionButton>
                )}
                
                <QuickActionButton to="/gift">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="8" width="18" height="12" rx="2"></rect>
                    <path d="M20 8H4a2 2 0 0 1-2-2V6c0-1.1.9-2 2-2h16a2 2 0 0 1 2 2v0c0 1.1-.9 2-2 2z"></path>
                    <path d="M12 19v-9"></path>
                    <path d="M8 12l4-3 4 3"></path>
                  </svg>
                  <ActionText>
                    <h4>Gift a Sleep Plan</h4>
                    <p>Share the gift of sleep with someone you care about</p>
                  </ActionText>
                </QuickActionButton>
                
                <QuickActionButton to="/contact">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                  </svg>
                  <ActionText>
                    <h4>Get Support</h4>
                    <p>Contact our team for assistance</p>
                  </ActionText>
                </QuickActionButton>
                
                <QuickActionButton to="/faq">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <ActionText>
                    <h4>FAQ</h4>
                    <p>Find answers to common questions</p>
                  </ActionText>
                </QuickActionButton>
              </QuickActions>
            </Card>
          </Sidebar>
        </DashboardGrid>
        
        {/* Add RecommendedProducts component 
        <RecommendedProducts 
          title="Recommended Products" 
          subtitle="Most bought products " 
        />*/}
        
      </DashboardContainer>
    </div>
  );
};

export default Dashboard;
